import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Check, AlertCircle, RefreshCw, Power } from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { Spinner } from '../../design-system';

interface MCPServerConfig {
  id: string;
  name: string;
  type: 'streamable_http' | 'sse';
  url?: string;
  headers: Record<string, string>; // Headers support
  env: Record<string, string>;
  auth_type?: 'none' | 'oauth';
  oauth_scopes?: string[];
  enabled: boolean;
  status: 'pending' | 'verified' | 'failed';
  error_message?: string;
}

interface McpServersSectionProps {
  workspace: any;
  workspaceId: string;
  api: any;
}

const McpServersSection: React.FC<McpServersSectionProps> = ({
  workspace,
  workspaceId,
  api,
}) => {
  const [servers, setServers] = useState<MCPServerConfig[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentServer, setCurrentServer] = useState<Partial<MCPServerConfig>>({});
  // Local state for editing headers and env as text
  const [headersText, setHeadersText] = useState("");
  const [envText, setEnvText] = useState("");
  const [authType, setAuthType] = useState<'none' | 'oauth'>('none');
  
  const [isVerifying, setIsVerifying] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>("");

  const renderVerifyButton = (server: MCPServerConfig) => {
    const loading = isVerifying === server.id;
    let label = 'Verify';
    let icon = <RefreshCw size={16} />;
    let classes =
      'bg-ui-background-secondary text-ui-text-secondary hover:bg-ui-background-tertiary';

    if (loading) {
      label = 'Verifying...';
      icon = <Spinner size={16} tone="current" />;
      classes = 'bg-ui-background-secondary text-ui-text-tertiary cursor-wait';
    } else if (server.status === 'verified') {
      label = 'Verified';
      icon = <Check size={16} />;
      classes =
        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50';
    } else if (server.status === 'failed') {
      label = 'Verification failed';
      icon = <AlertCircle size={16} />;
      classes =
        'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50';
    }

    return (
      <button
        onClick={() => handleVerify(server)}
        disabled={loading}
        className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${classes}`}
        title="Verify server connection"
      >
        {icon}
        {label}
      </button>
    );
  };

  const renderEnabledToggle = (server: MCPServerConfig) => {
    const disabledReason = !server.enabled && server.status !== 'verified'
        ? 'Verify the server before enabling'
        : '';
    const isDisabled = Boolean(disabledReason) || isVerifying === server.id;
    const label = server.enabled ? 'Disable' : 'Enable';
    const classes = server.enabled
      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
      : 'bg-ui-background-secondary text-ui-text-secondary hover:bg-ui-background-tertiary';

    return (
      <button
        onClick={() => handleToggleEnabled(server)}
        disabled={isDisabled}
        className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${classes}`}
        title={disabledReason || (server.enabled ? 'Disable this server' : 'Enable this server')}
      >
        <Power size={16} />
        {label}
      </button>
    );
  };

  // Initialize servers from workspace
  useEffect(() => {
    if (workspace && workspace.mcp_servers) {
      setServers(workspace.mcp_servers);
    }
  }, [workspace]);

  const handleSaveServers = async (updatedServers: MCPServerConfig[]) => {
    setIsSaving(true);
    setError(null);
    try {
      const response = await api.put(`/workspaces/${workspaceId}`, {
        mcp_servers: updatedServers
      });
      setServers(updatedServers);
      setSuccessMessage("MCP servers updated successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err: any) {
      console.error("Failed to save MCP servers:", err);
      setError(err.message || "Failed to save MCP servers");
    } finally {
      setIsSaving(false);
    }
  };

  const handleVerify = async (server: MCPServerConfig): Promise<boolean> => {
    if (isVerifying) return false;
    setIsVerifying(server.id);
    setError(null);

    try {
      const response = await api.post(
        `/workspaces/${workspaceId}/mcp/verify`,
        server
      ) as { success: boolean; error?: string; authorization_url?: string; state?: string };
      
      const updatedServer = { ...server };
      const isSuccess = response.success;
      
      if (isSuccess) {
        updatedServer.status = 'verified';
        updatedServer.error_message = undefined;
      } else if (response.error === 'oauth_required' && response.authorization_url) {
        // Start OAuth by opening the auth URL; keep server pending
        window.open(response.authorization_url, '_blank', 'noopener,noreferrer');
        updatedServer.status = 'pending';
        updatedServer.error_message = "OAuth authorization required. Complete the popup, then re-verify.";
      } else {
        updatedServer.status = 'failed';
        updatedServer.error_message = response.error || "Verification failed";
        updatedServer.enabled = false; // Disable if failed
      }

      const updatedServers = servers.map(s => s.id === server.id ? updatedServer : s);
      setServers(updatedServers);
      
      // Persist the status update
      await handleSaveServers(updatedServers);
      return isSuccess;

    } catch (err: any) {
      console.error("Verification error:", err);
      const updatedServer = { 
        ...server, 
        status: 'failed' as const, 
        error_message: err.message || "Verification request failed",
        enabled: false
      };
      const updatedServers = servers.map(s => s.id === server.id ? updatedServer : s);
      setServers(updatedServers);
      return false;
    } finally {
      setIsVerifying(null);
    }
  };

  const handleAddServer = () => {
    setCurrentServer({
      id: crypto.randomUUID(),
      name: '',
    type: 'streamable_http',
      url: '',
      headers: {},
      env: {},
      auth_type: 'none',
      oauth_scopes: [],
      enabled: false,
      status: 'pending'
    });
    setHeadersText("");
    setEnvText("");
    setAuthType('none');
    setIsEditing(true);
  };

  const handleEditServer = (server: MCPServerConfig) => {
    setCurrentServer({ ...server });
    setHeadersText(Object.entries(server.headers || {}).map(([k, v]) => `${k}=${v}`).join('\n'));
    setEnvText(Object.entries(server.env || {}).map(([k, v]) => `${k}=${v}`).join('\n'));
    setAuthType(server.auth_type === 'oauth' ? 'oauth' : 'none');
    setIsEditing(true);
  };

  const handleDeleteServer = async (serverId: string) => {
    if (!window.confirm("Are you sure you want to delete this MCP server?")) return;
    const updatedServers = servers.filter(s => s.id !== serverId);
    await handleSaveServers(updatedServers);
  };

  const handleToggleEnabled = async (server: MCPServerConfig) => {
    if (server.status !== 'verified' && !server.enabled) {
        // If trying to enable an unverified server, verify it first
        const verified = await handleVerify(server);
        
        // If verification failed, don't proceed to enable
        if (!verified) {
            alert("Please verify the server connection before enabling.");
            return;
        }
        // If just verified, user needs to toggle again (safe approach)
        return;
    }
    
    const updatedServers = servers.map(s => 
      s.id === server.id ? { ...s, enabled: !s.enabled } : s
    );
    await handleSaveServers(updatedServers);
  };

  const handleSaveEdit = async () => {
    if (!currentServer.name) {
      setError("Name is required");
      return;
    }
    if (!currentServer.url) {
      setError("URL is required");
      return;
    }

    // Parse headers and env
    const parseProperties = (text: string): Record<string, string> => {
        const result: Record<string, string> = {};
        text.split('\n').forEach(line => {
            if (!line.includes('=')) return;
            const parts = line.split('=');
            const k = parts[0].trim();
            const v = parts.slice(1).join('=').trim();
            if (k) result[k] = v;
        });
        return result;
    };

    const headers = parseProperties(headersText);
    const env = parseProperties(envText);

    // Reset status to pending on edit
    const newServer = { 
        ...currentServer, 
        type: 'streamable_http',
        status: 'pending' as const, 
        enabled: false, // Disable on edit until re-verified
        error_message: undefined,
        headers: headers,
        env: env,
        auth_type: authType,
        oauth_scopes: []
    } as MCPServerConfig;

    let updatedServers;
    if (servers.find(s => s.id === newServer.id)) {
      updatedServers = servers.map(s => s.id === newServer.id ? newServer : s);
    } else {
      updatedServers = [...servers, newServer];
    }

    await handleSaveServers(updatedServers);
    setIsEditing(false);
    setCurrentServer({});
    setHeadersText("");
    setEnvText("");
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-ui-text-primary">
            MCP Servers
          </h2>
          <p className="text-sm text-ui-text-tertiary mt-1">
            Configure remote Model Context Protocol servers (streamable HTTP).
          </p>
        </div>
        <button
          onClick={handleAddServer}
          disabled={isEditing}
          className="flex items-center gap-2 px-4 py-2 bg-brand-primary hover:bg-brand-primary/90 text-ui-text-primary rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus size={18} />
          Add Server
        </button>
      </div>

      {successMessage && (
        <div className="mb-6 p-4 bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-xl">
          <p className="text-green-700 dark:text-green-400">{successMessage}</p>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-xl">
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {isEditing ? (
        <div className="bg-ui-background-tertiary dark:bg-neutral-800/50 p-6 rounded-xl border border-ui-border-primary space-y-6">
          <h3 className="text-lg font-medium text-ui-text-primary mb-4">
            {currentServer.id && servers.find(s => s.id === currentServer.id) ? 'Edit Server' : 'Add New Server'}
          </h3>
          
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-ui-text-secondary mb-1">Name</label>
              <input
                type="text"
                value={currentServer.name || ''}
                onChange={e => setCurrentServer(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-2 bg-ui-background-secondary border border-ui-border-primary rounded-lg text-ui-text-primary placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary"
                placeholder="My Server"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ui-text-secondary mb-1">URL</label>
              <input
                type="text"
                value={currentServer.url || ''}
                onChange={e => setCurrentServer(prev => ({ ...prev, url: e.target.value }))}
                className="w-full px-4 py-2 bg-ui-background-secondary border border-ui-border-primary rounded-lg font-mono text-sm text-ui-text-primary placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary"
                placeholder="https://api.example.com/mcp"
              />
            </div>

            <div>
                <label className="block text-sm font-medium text-ui-text-secondary mb-1">Headers (KEY=VALUE per line)</label>
                <textarea
                value={headersText}
                onChange={e => setHeadersText(e.target.value)}
                className="w-full px-4 py-2 bg-ui-background-secondary border border-ui-border-primary rounded-lg font-mono text-sm min-h-[100px] text-ui-text-primary placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary"
                placeholder="Authorization=Bearer token&#10;Accept=application/json"
                />
            </div>

            {/* Environment Variables (Still supported generally, though less common for remote) */}
            <div>
                <label className="block text-sm font-medium text-ui-text-secondary mb-1">Environment Variables (KEY=VALUE per line)</label>
        <textarea
        value={envText}
        onChange={e => setEnvText(e.target.value)}
        className="w-full px-4 py-2 bg-ui-background-secondary border border-ui-border-primary rounded-lg font-mono text-sm min-h-[100px] text-ui-text-primary placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary"
        placeholder="CUSTOM_VAR=value"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-ui-text-secondary mb-1">Authentication</label>
          <select
            value={authType}
            onChange={e => setAuthType(e.target.value as 'none' | 'oauth')}
            className="w-full px-4 py-2 bg-ui-background-secondary border border-ui-border-primary rounded-lg text-ui-text-primary"
          >
            <option value="none">None (use headers above for static auth)</option>
            <option value="oauth">OAuth (Sentry, etc.)</option>
          </select>
          <p className="text-xs text-ui-text-tertiary mt-1">
            Choose OAuth for servers that require a redirect-based login flow.
          </p>
        </div>

      </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 text-ui-text-secondary hover:bg-ui-background-tertiary rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={isSaving}
              className="px-4 py-2 bg-brand-action hover:bg-brand-action-hover text-white rounded-lg font-medium"
            >
              {isSaving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {servers.length === 0 ? (
            <div className="text-center py-12 bg-ui-background-tertiary dark:bg-neutral-800/30 rounded-xl border border-dashed border-ui-border-primary">
              <p className="text-ui-text-tertiary">No MCP servers configured</p>
              <button
                onClick={handleAddServer}
                className="mt-4 text-brand-primary hover:underline"
              >
                Add your first server
              </button>
            </div>
          ) : (
            servers.map(server => (
              <div key={server.id} className="bg-ui-background-secondary p-4 rounded-xl border border-ui-border-primary flex flex-col gap-3">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-ui-text-primary flex items-center gap-2">
                        {server.name}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                            server.status === 'verified' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            server.status === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                            'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                            {server.status}
                        </span>
                    </h3>
                    <p className="text-sm text-ui-text-tertiary font-mono mt-1 truncate max-w-md">
                      {server.url}
                    </p>
                    {server.error_message && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1 flex items-center gap-1">
                            <AlertCircle size={12} /> {server.error_message}
                        </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {renderVerifyButton(server)}
                    {renderEnabledToggle(server)}
                    <button
                      onClick={() => handleEditServer(server)}
                      className="p-2 hover:bg-ui-background-tertiary rounded-lg text-ui-text-tertiary"
                      title="Edit configuration"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteServer(server.id)}
                      className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg text-red-500 dark:text-red-400"
                      title="Delete server"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default McpServersSection;
